import React, { useEffect, useState, useContext } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../Common/Button/Button";
import SidePopup from "../../../Common/SidePopup/SidePopup";
import DynamicSelectInput from "../../../Common/Select-Input/SelectInput";
import DynamicTextField from "../../../Common/Textfield/Textfield";
import PhoneNumberInput from "../../../Common/PhoneNumberInput/PhoneNumberInput";
import { post, get, deleteRequest, put } from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  SUBMIT_FINANCIAL_CREATE,
  GET_FINANCIAL_LIST,
  DELETE_FINANCIAL_PROFILE,
  UPDATE_FINANCIAL_PROFILE,
} from "../../../helper/url_helper";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import Table from "../../../Common/Table/Table";
import editIcon from "../../../Assets/Images/cf-edit-icon-dark.svg";
import deleteIcon from "../../../Assets/Images/cf-delete-icon-dark.svg";
import editLightIcon from "../../../Assets/Images/cf-edit-icon-light.svg";
import deleteLightIcon from "../../../Assets/Images/cf-delete-icon-light.svg";
import { DynamicContext } from "../../../Context/DynamicContext";
import { ConfirmationPopup } from "../../../Common/PopupStyles/PopupComponent";
import { createPortal } from "react-dom";
import { TENANT_PRIME_API_KEY } from "../../../Tenant-Config/TenantConfig";
import { filterPayload } from "../../../Common/Common-Function/CommonFunctions";

const countryValues = [
  { title: "India", value: "INDIA" },
  { title: "United States", value: "USA" },
  { title: "Canada", value: "CANADA" },
];

const inputStyle = {
  borderRadius: "12px",
  color: "var(--header__color)",
  fontSize: "14px",
  height: "38px",
  padding: "0 12px",
};

const labelStyle = {
  color: "var(--sidepopup-label-color)",
  fontSize: "12px",
  fontWeight: 500,
  marginBottom: "6px",
};

const buildFinancialProfilePayload = (values) => {
  const payload = filterPayload(values);

  if (!values.phone?.trim()) {
    delete payload.phone;
  }

  return payload;
};

function FinancialProfile() {
  const { isDarkMode } = useContext(DynamicContext);
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [financeValue, setFinanceValue] = useState([]);
  const [deleteProfileId, setDeleteProfileId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const createFinancialProfile = async (values, resetForm) => {
    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };

      await post(
        VYLABS_BASE_URL,
        SUBMIT_FINANCIAL_CREATE,
        buildFinancialProfilePayload(values),
        requestConfig,
      );
      Toast("success", "Financial profile created successfully");
      await getFinancialList();
      setIsProfilePopupOpen(false);
      resetForm();
    } catch (error) {
      console.error("Financial profile create failed:", error);
      Toast(
        "error",
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to create financial profile",
      );
    }
  };

  const updateFinancialProfile = async (values, resetForm) => {
    if (!selectedProfile?.id) return;

    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };
      await put(
        VYLABS_BASE_URL,
        `${UPDATE_FINANCIAL_PROFILE}?id=${encodeURIComponent(
          selectedProfile.id,
        )}`,
        buildFinancialProfilePayload(values),
        requestConfig,
      );
      Toast("success", "Financial profile updated successfully");
      await getFinancialList();
      setIsProfilePopupOpen(false);
      setSelectedProfile(null);
      resetForm();
    } catch (error) {
      console.error("Financial profile update failed:", error);
      Toast(
        "error",
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to update financial profile",
      );
    }
  };

  const financialFormik = useFormik({
    initialValues: {
      account_name: "",
      account_number: "",
      address_line1: "",
      address_line2: "",
      bank_branch: "",
      bank_name: "",
      business_number: "",
      city: "",
      country: "",
      ein: "",
      email: "",
      gst_hst_number: "",
      gstin: "",
      ifsc_code: "",
      is_default: true,
      legal_name: "",
      pan: "",
      phone: "",
      postal_code: "",
      profile_name: "",
      province_tax_number: "",
      remit_to_address_line1: "",
      remit_to_address_line2: "",
      remit_to_city: "",
      remit_to_country: "",
      remit_to_name: "",
      remit_to_postal_code: "",
      remit_to_state_or_province: "",
      routing_number: "",
      state_or_province: "",
      swift_bic: "",
    },
    validationSchema: Yup.object({
      profile_name: Yup.string().required("Profile name is required"),
      country: Yup.string().required("Country is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (selectedProfile?.id) {
        await updateFinancialProfile(values, resetForm);
        return;
      }

      await createFinancialProfile(values, resetForm);
    },
  });

  const handleSubmitFinancialProfile = async (event) => {
    event.preventDefault();
    const errors = await financialFormik.validateForm();

    if (Object.keys(errors).length > 0) {
      financialFormik.setTouched(
        {
          profile_name: true,
          country: true,
        },
        false,
      );
      Toast("error", "Please fill all required fields correctly");
      return;
    }

    financialFormik.handleSubmit(event);
  };

  const getFinancialList = async () => {
    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };
      setIsLoading(true);
      const response = await get(
        VYLABS_BASE_URL,
        GET_FINANCIAL_LIST,
        requestConfig,
      );
      const data = Array.isArray(response?.financial_profiles)
        ? response.financial_profiles
        : [];
      setFinanceValue(data);
    } catch (error) {
      console.error("financial profile", error);
      setFinanceValue([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!deleteProfileId) return;
    try {
      const response = await deleteRequest(
        VYLABS_BASE_URL,
        `${DELETE_FINANCIAL_PROFILE}?id=${deleteProfileId}`,
        {
          id: deleteProfileId,
        },
        {
          headers: {
            "X-API-Key": TENANT_PRIME_API_KEY,
          },
        },
      );
      if (response) {
        Toast("success", "Financial profile deleted successfully");
        getFinancialList();
        setIsDeleteOpen(false);
        setDeleteProfileId(null);
      }
    } catch (error) {
      console.error("error in deleteFinancialProfile:", error);
      Toast("error", "Failed to delete financial profile");
    }
  };

  const handleOpenCreateProfile = () => {
    setSelectedProfile(null);
    financialFormik.resetForm();
    setIsProfilePopupOpen(true);
  };

  const handleEditProfile = (row) => {
    setSelectedProfile(row);
    financialFormik.setValues({
      ...financialFormik.initialValues,
      account_name: row.account_name || "",
      account_number: row.account_number || "",
      address_line1: row.address_line1 || "",
      address_line2: row.address_line2 || "",
      bank_branch: row.bank_branch || "",
      bank_name: row.bank_name || "",
      business_number: row.business_number || "",
      city: row.city || "",
      country: row.country || "",
      ein: row.ein || "",
      email: row.email || "",
      gst_hst_number: row.gst_hst_number || "",
      gstin: row.gstin || "",
      ifsc_code: row.ifsc_code || "",
      is_default:
        row.is_default === undefined || row.is_default === null
          ? true
          : row.is_default,
      legal_name: row.legal_name || "",
      pan: row.pan || "",
      phone: row.phone || "",
      postal_code: row.postal_code || "",
      profile_name: row.profile_name || "",
      province_tax_number: row.province_tax_number || "",
      remit_to_address_line1: row.remit_to_address_line1 || "",
      remit_to_address_line2: row.remit_to_address_line2 || "",
      remit_to_city: row.remit_to_city || "",
      remit_to_country: row.remit_to_country || "",
      remit_to_name: row.remit_to_name || "",
      remit_to_postal_code: row.remit_to_postal_code || "",
      remit_to_state_or_province: row.remit_to_state_or_province || "",
      routing_number: row.routing_number || "",
      state_or_province: row.state_or_province || "",
      swift_bic: row.swift_bic || "",
    });
    setIsProfilePopupOpen(true);
  };

  useEffect(() => {
    getFinancialList();
  }, []);

  const profileColumns = [
    {
      name: "Profile Name",
      selector: (row) => row.profile_name || "--",
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Legal Name",
      selector: (row) => row.legal_name || "--",
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Country",
      selector: (row) => row.country || "--",
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Email",
      selector: (row) => row.email || "--",
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "--",
      minWidth: "150px",
    },
    {
      name: "Bank",
      selector: (row) => row.bank_name || "--",
      sortable: true,
      minWidth: "160px",
    },
    {
      name: "Actions",
      minWidth: "110px",
      cell: (row) => (
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "80px",
          }}
        >
          <img
            src={isDarkMode ? editIcon : editLightIcon}
            alt="Edit"
            style={{
              cursor: "pointer",
              opacity: 0.7,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 1)}
            onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
            onClick={(e) => {
              e.stopPropagation();
              handleEditProfile(row);
            }}
          />
          <img
            src={isDarkMode ? deleteIcon : deleteLightIcon}
            alt="Delete"
            style={{
              cursor: "pointer",
              opacity: 0.7,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 1)}
            onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteProfileId(row.id);
              setIsDeleteOpen(true);
            }}
          />
        </div>
      ),
    },
  ];

  const getFieldError = (fieldName) =>
    financialFormik.touched[fieldName] &&
    Boolean(financialFormik.errors[fieldName]);

  const handleCountryChange = (event) => {
    const value = event.target.value;

    financialFormik.setFieldValue("country", value);
    if (value) {
      financialFormik.setFieldError("country", "");
    }
  };

  const handleCloseProfilePopup = () => {
    setIsProfilePopupOpen(false);
    setSelectedProfile(null);
    financialFormik.resetForm();
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "18px",
        }}
      >
        <Button label="Add Profile" event={handleOpenCreateProfile} />
      </div>
      <div>
        <Table
          columns={profileColumns}
          data={financeValue}
          progressPending={isLoading}
          dataNotFoundMessage="No financial profiles found"
        />
      </div>

      {createPortal(
      <SidePopup
        isOpen={isProfilePopupOpen}
        onClose={handleCloseProfilePopup}
        title={"Financial Profile"}
        sidePopupWidth="70%"
        contentPadding="24px"
      >
        <form
          onSubmit={handleSubmitFinancialProfile}
          style={{ padding: 0 }}
          noValidate
        >
          <div
            style={{
              alignItems: "flex-start",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "26px",
            }}
          >
            <div>
              <h3
                style={{
                  color: "var(--header__color)",
                  fontSize: "16px",
                  fontWeight: 600,
                  margin: "0 0 8px",
                }}
              >
                Company Information
              </h3>
              <p
                style={{
                  color: "var(--grid-na-text)",
                  fontSize: "13px",
                  margin: 0,
                }}
              >
                These details will appear on invoices generated by your
                organization.
              </p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px 16px",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            }}
          >
            <DynamicTextField
              label="Profile Name"
              name="profile_name"
              value={financialFormik.values.profile_name}
              onChange={financialFormik.handleChange}
              onBlur={() => financialFormik.setFieldTouched("profile_name", true)}
              required={true}
              error={
                financialFormik.touched.profile_name &&
                financialFormik.errors.profile_name
              }
              errorMessage={financialFormik.errors.profile_name}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicTextField
              label="Legal Name"
              name="legal_name"
              value={financialFormik.values.legal_name}
              onChange={financialFormik.handleChange}
              onBlur={financialFormik.handleBlur}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicTextField
              label="Email"
              name="email"
              value={financialFormik.values.email}
              onChange={financialFormik.handleChange}
              onBlur={financialFormik.handleBlur}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <PhoneNumberInput
              label="Phone"
              name="phone"
              value={financialFormik.values.phone}
              onChange={(phone) =>
                financialFormik.setFieldValue("phone", phone)
              }
              onBlur={() => financialFormik.setFieldTouched("phone", true)}
              labelStyle={labelStyle}
              phoneContainerStyle={{ width: "100%" }}
              height="38px"
            />
            <DynamicSelectInput
              label="Country"
              name="country"
              value={financialFormik.values.country}
              options={countryValues}
              onChange={handleCountryChange}
              onBlur={() => financialFormik.setFieldTouched("country", true)}
              required
              error={
                financialFormik.touched.country &&
                financialFormik.errors.country
              }
              errorMessage={financialFormik.errors.country}
              labelStyle={labelStyle}
              menuWidth={360}
              selectStyle={{
                border: "none",
                borderRadius: "12px",
                boxShadow: "none",
                color: "var(--header__color)",
                fontSize: "14px",
              }}
              selectSx={{
                "&&": {
                  border: "none !important",
                  boxShadow: "none !important",
                },
                "&.MuiOutlinedInput-root": {
                  border: "none !important",
                  boxShadow: "none !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid var(--border__color) !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid rgb(142, 136, 244) !important",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #6860ee !important",
                },
              }}
            />
            <DynamicTextField
              label="Address Line 1"
              name="address_line1"
              value={financialFormik.values.address_line1}
              onChange={financialFormik.handleChange}
              onBlur={financialFormik.handleBlur}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicTextField
              label="Address Line 2"
              name="address_line2"
              value={financialFormik.values.address_line2}
              onChange={financialFormik.handleChange}
              onBlur={financialFormik.handleBlur}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicTextField
              label="City"
              name="city"
              value={financialFormik.values.city}
              onChange={financialFormik.handleChange}
              onBlur={financialFormik.handleBlur}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicTextField
              label="State"
              name="state_or_province"
              value={financialFormik.values.state_or_province}
              onChange={financialFormik.handleChange}
              onBlur={financialFormik.handleBlur}
              labelStyle={labelStyle}
              style={inputStyle}
            />
            <DynamicTextField
              label="PIN Code"
              name="postal_code"
              value={financialFormik.values.postal_code}
              onChange={financialFormik.handleChange}
              onBlur={financialFormik.handleBlur}
              labelStyle={labelStyle}
              style={inputStyle}
            />
          </div>

          <div
            style={{
              border: "1px solid var(--border__color)",
              borderRadius: "14px",
              boxShadow: "0 1px 4px rgba(15, 23, 42, 0.04)",
              marginTop: "18px",
              padding: "22px 20px",
            }}
          >
            <div style={{ marginBottom: "22px" }}>
              <h3
                style={{
                  color: "var(--header__color)",
                  fontSize: "15px",
                  fontWeight: 600,
                  margin: "0 0 8px",
                }}
              >
                Tax Registration
              </h3>
              <p
                style={{
                  color: "var(--grid-na-text)",
                  fontSize: "13px",
                  margin: 0,
                }}
              >
                Add the tax registration details required for your country.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gap: "18px 16px",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              }}
            >
              <DynamicTextField
                label="GSTIN"
                name="gstin"
                value={financialFormik.values.gstin}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                labelStyle={labelStyle}
                style={inputStyle}
              />
              <DynamicTextField
                label="PAN"
                name="pan"
                value={financialFormik.values.pan}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                labelStyle={labelStyle}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              border: "1px solid var(--border__color)",
              borderRadius: "14px",
              boxShadow: "0 1px 4px rgba(15, 23, 42, 0.04)",
              marginTop: "18px",
              padding: "22px 20px",
            }}
          >
            <div style={{ marginBottom: "22px" }}>
              <h3
                style={{
                  color: "var(--header__color)",
                  fontSize: "15px",
                  fontWeight: 600,
                  margin: "0 0 8px",
                }}
              >
                Payment Details
              </h3>
              <p
                style={{
                  color: "var(--grid-na-text)",
                  fontSize: "13px",
                  margin: 0,
                }}
              >
                Add the bank information you want customers to see when making
                invoice payments.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gap: "18px 16px",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              }}
            >
              <DynamicTextField
                label="Bank Name"
                name="bank_name"
                value={financialFormik.values.bank_name}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                labelStyle={labelStyle}
                style={inputStyle}
              />
              <DynamicTextField
                label="Branch"
                name="bank_branch"
                value={financialFormik.values.bank_branch}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                labelStyle={labelStyle}
                style={inputStyle}
              />
              <DynamicTextField
                label="Account Name"
                name="account_name"
                value={financialFormik.values.account_name}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                error={getFieldError("account_name")}
                errorMessage={financialFormik.errors.account_name}
                labelStyle={labelStyle}
                style={inputStyle}
              />
              <DynamicTextField
                label="Account Number"
                name="account_number"
                value={financialFormik.values.account_number}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                labelStyle={labelStyle}
                style={inputStyle}
              />
              <DynamicTextField
                label="IFSC Code"
                name="ifsc_code"
                value={financialFormik.values.ifsc_code}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                labelStyle={labelStyle}
                style={inputStyle}
              />
              <DynamicTextField
                label="SWIFT / BIC"
                name="swift_bic"
                value={financialFormik.values.swift_bic}
                onChange={financialFormik.handleChange}
                onBlur={financialFormik.handleBlur}
                labelStyle={labelStyle}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "24px",
            }}
          >
            <Button
              type="button"
              label="Cancel"
              isCancel
              event={handleCloseProfilePopup}
            />
            <Button
              type="submit"
              label={
                financialFormik.isSubmitting
                  ? selectedProfile
                    ? "Updating..."
                    : "Creating..."
                  : selectedProfile
                    ? "Update"
                    : "Create"
              }
              disabled={financialFormik.isSubmitting}
            />
          </div>
        </form>
      </SidePopup>,
        document.body,
      )}
      {createPortal(
        <ConfirmationPopup
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setDeleteProfileId(null);
          }}
          title="Delete Financial Profile"
          subTitle="This financial profile will be permanently deleted and cannot be recovered."
          message="Are you sure you want to delete this financial profile?"
          confirmLabel="Delete"
          onConfirm={handleDeleteProfile}
        />,
        document.body,
      )}
    </div>
  );
}

export default FinancialProfile;
